import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("oms_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { localStorage.removeItem("oms_token"); setUser(null); })
      .finally(() => setLoading(false));
  }, []);

  async function login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("oms_token", data.token);
    setUser(data.user);
    return data.user;
  }

  async function logout() {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("oms_token");
    setUser(null);
  }

  /**
   * Role-based UI guard — LEGACY. Kept for backward compatibility.
   * New code should prefer `hasModule(key)` / `canAction(key)` / `cvShow(key)`
   * which read the permission model from `effective_permissions`.
   */
  const can = (roles) => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    if (roles.includes(user.role)) return true;
    // Admin retains legacy universal access unless the action is super_admin-only
    if (user.role === "admin" && !(roles.length === 1 && roles[0] === "super_admin")) return true;
    return false;
  };

  /** Returns the effective permissions object (falls back to a minimal object
   *  when user or backend didn't return one yet). */
  const perms = () => user?.effective_permissions || { modules: [], actions: [], commercial_visibility: { show_prices: true, show_totals: true, show_gst: true, show_payment_status: true } };

  /** True iff the user's permissions include the given module key. */
  const hasModule = (key) => {
    if (!user) return false;
    if (user.role === "super_admin") return true;  // defence-in-depth
    return (perms().modules || []).includes(key);
  };

  /** True iff the user's permissions include the given action key. */
  const canAction = (key) => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    return (perms().actions || []).includes(key);
  };

  /** True iff the user's commercial visibility toggle is on.
   *  key ∈ "show_prices" | "show_totals" | "show_gst" | "show_payment_status" */
  const cvShow = (key) => {
    const cv = perms().commercial_visibility || {};
    return cv[key] !== false;  // default-on if unset
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, can, hasModule, canAction, cvShow }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
}
